import { Permission } from 'src/app/core/models/users.model';

/**
 * Utility functions for handling permission checks
 */
export class PermissionsUtils {
  /**
   * Check if user has a specific permission or an array of permissions
   * @param userPermissions - Array of permissions that the user has
   * @param requiredPermission - Permission(s) to check for
   * @returns boolean indicating if user has permission
   * When an array of permissions is provided, checks if user has ALL of the required permissions
   */
  static hasPermission(
    userPermissions: Permission[] | string[],
    requiredPermission: string | string[]
  ): boolean {
    if (!userPermissions || userPermissions.length === 0) {
      return false;
    }

    const normalizedPermissions = userPermissions as readonly string[];

    if (Array.isArray(requiredPermission)) {
      return requiredPermission.every((permission) =>
        normalizedPermissions.includes(permission)
      );
    }

    return normalizedPermissions.includes(requiredPermission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param userPermissions - Array of permissions that the user has
   * @param requiredPermissions - Array of permissions to check for
   * @returns boolean indicating if user has any of the permissions
   */
  static hasAnyPermission(
    userPermissions: Permission[] | string[],
    requiredPermissions: string[]
  ): boolean {
    if (!userPermissions || userPermissions.length === 0) {
      return false;
    }

    return requiredPermissions.some((permission) =>
      this.hasPermission(userPermissions, permission)
    );
  }

  /**
   * Check if user has all of the specified permissions
   * @param userPermissions - Array of permissions that the user has
   * @param requiredPermissions - Array of permissions to check for
   * @returns boolean indicating if user has all of the permissions
   */
  static hasAllPermissions(
    userPermissions: Permission[] | string[],
    requiredPermissions: string[]
  ): boolean {
    if (!userPermissions || userPermissions.length === 0) {
      return false;
    }

    return requiredPermissions.every((permission) =>
      this.hasPermission(userPermissions, permission)
    );
  }

  /**
   * Get a mapping of old permission names to new standardized permission names
   * @returns Object mapping old permission names to new ones
   */
  static getPermissionMappings(): Record<string, string> {
    return {
      list_employers: 'view_employees',
      register_employer: 'manage_employees',
      list_patient: 'view_all_clients',
      register_patient: 'create_clients',
      edit_patient: 'update_clients',
      list_patient_log_report: 'manage_notes',
      list_parent: 'view_parents',
      register_parent: 'manage_parents',
      edit_parent: 'manage_parents',
      list_insurance: 'manage_insurances',
      register_insurance: 'manage_insurances',
      list_location: 'manage_locations',
      register_location: 'manage_locations',
      edit_location: 'manage_locations',
      logs: 'manage_notes',
      claims: 'manage_claims',
      list_bip: 'view_bips',
      register_bip: 'manage_bips',
      edit_bip: 'manage_bips',
      view_bip: 'view_bips',
      register_rol: 'manage_roles',
      list_rol: 'manage_roles',
      admin_dashboard: 'access_all_locations',
    };
  }

  /**
   * Convert old permission name to new standardized name if mapping exists
   * @param oldPermission - Old permission name
   * @returns New standardized permission name or original if no mapping exists
   */
  static mapToStandardPermission(oldPermission: string): string {
    const mappings = this.getPermissionMappings();
    return mappings[oldPermission] || oldPermission;
  }
}
